import { Link } from "react-router-dom";
import { ArrowRight, Compass, Home as HomeIcon } from "lucide-react";
import { Button } from "../components/ui/button";
import { useSEO } from "../hooks/useSEO";
import { media } from "../data/siteData";

export default function NotFound() {
  useSEO({
    title: "Page not found",
    description: "The page you’re looking for has drifted out with the tide.",
    path: "/404",
  });

  return (
    <main data-testid="not-found-page" className="relative isolate overflow-hidden bg-[#0A0F1C] px-5 pb-20 pt-32 text-white md:px-10 md:pb-28">
      <div
        aria-hidden="true"
        className="absolute inset-0 -z-10 opacity-40"
        style={{ backgroundImage: `url(${media.heroTexture})`, backgroundSize: "cover", backgroundPosition: "center" }}
      />
      <div
        aria-hidden="true"
        className="absolute inset-0 -z-10 bg-[radial-gradient(circle_at_75%_15%,rgba(255,94,58,0.35),transparent_38%),radial-gradient(circle_at_15%_85%,rgba(217,70,239,0.18),transparent_45%),linear-gradient(180deg,rgba(10,15,28,0.45),#0A0F1C_85%)]"
      />

      <section className="mx-auto grid max-w-6xl items-center gap-12 lg:grid-cols-[1.1fr_0.9fr]">
        <div className="animate-rise">
          <p data-testid="not-found-kicker" className="text-xs font-bold uppercase tracking-[0.32em] text-[#FF5E3A]">
            404 — drifted off shore
          </p>
          <h1 data-testid="not-found-title" className="mt-6 font-heading text-5xl font-black leading-none tracking-tighter sm:text-6xl lg:text-7xl">
            That wave already broke.
          </h1>
          <p data-testid="not-found-description" className="mt-7 max-w-xl text-base leading-8 text-slate-300 md:text-lg">
            The link you followed has drifted out with the tide. Paddle back to dry land and pick a fresh route — the next set is rolling in.
          </p>

          <div className="mt-10 flex flex-col gap-4 sm:flex-row">
            <Button data-testid="not-found-home-button" asChild className="h-12 rounded-xl bg-[#FF5E3A] px-8 font-semibold uppercase tracking-[0.16em] text-white shadow-[0_0_28px_rgba(255,94,58,0.22)] transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#FF7B5C]">
              <Link to="/"><HomeIcon className="mr-2 h-4 w-4" />Back to home</Link>
            </Button>
            <Button data-testid="not-found-activities-button" asChild variant="outline" className="h-12 rounded-xl border-white/15 bg-white/5 px-8 font-semibold uppercase tracking-[0.16em] text-white backdrop-blur transition-all duration-300 hover:-translate-y-0.5 hover:border-white/30 hover:bg-white/10 hover:text-white">
              <Link to="/activities">See activities</Link>
            </Button>
          </div>

          <div data-testid="not-found-quick-links" className="mt-10 grid gap-3 sm:grid-cols-2">
            <Link data-testid="not-found-link-accommodation" to="/accommodation" className="group flex items-center justify-between rounded-2xl border border-white/10 bg-white/[0.03] p-4 transition-all duration-300 hover:border-white/25 hover:bg-white/5">
              <span className="text-sm font-medium text-white">Accommodation</span>
              <ArrowRight className="h-4 w-4 text-[#FF5E3A] transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <Link data-testid="not-found-link-contact" to="/contact" className="group flex items-center justify-between rounded-2xl border border-white/10 bg-white/[0.03] p-4 transition-all duration-300 hover:border-white/25 hover:bg-white/5">
              <span className="text-sm font-medium text-white">Contact the club</span>
              <ArrowRight className="h-4 w-4 text-[#FF5E3A] transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </div>

        <div data-testid="not-found-visual" className="relative animate-rise-delay">
          <div className="absolute -left-6 -top-6 h-40 w-40 bg-[#D946EF]/25 blur-3xl" />
          <div className="relative overflow-hidden rounded-[2rem] border border-white/10 bg-white/5 p-3 shadow-2xl shadow-black/40 backdrop-blur-xl">
            <img
              data-testid="not-found-image"
              src={media.heroSurf}
              alt="Surfer paddling out at sunrise on the Kannur coast"
              loading="lazy"
              decoding="async"
              className="aspect-[4/5] w-full rounded-[1.5rem] object-cover object-center"
            />
            <div className="absolute bottom-7 left-7 right-7 rounded-2xl border border-white/15 bg-[#0A0F1C]/78 p-5 backdrop-blur-xl">
              <p className="flex items-center gap-2 text-xs uppercase tracking-[0.24em] text-[#FFC059]">
                <Compass className="h-4 w-4" /> Find your way back
              </p>
              <p className="mt-2 font-heading text-xl font-bold text-white">Pick a route from the menu above.</p>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
